import InfoIcon from '@mui/icons-material/Info';
import {
  Button,
  Card,
  CardContent,
  DialogActions,
  DialogContent,
  Dialog as DialogCore,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LoadingButton } from '@/components/atoms/loading-button/loading-button';
import { DialogActions as DialogActionsInterface } from '@/components/molecules/dialog';
import { CompactPagination } from '@/components/organisms/compact-pagination';
import { useAttachHostToBillingGroup } from '@/features/billing-groups/hooks/mutation/use-attach-host-to-billing-group';
import { useRemoveHostFromBillingGroup } from '@/features/billing-groups/hooks/mutation/use-remove-host-from-billing-group';
import { useBillingGroups } from '@/features/billing-groups/hooks/query/use-billing-groups';
import { usePagination } from '@/hooks/use-pagination';

interface Props extends DialogActionsInterface {
  billingGroupId: number;
  hostId: number;
}

export const UpdateHostBillingGroupDialog = ({ onCancel, open, billingGroupId, hostId }: Props) => {
  const { t } = useTranslation(['hosts', 'billingGroups', 'common']);

  const navigate = useNavigate({ from: '/hosts/' });

  const {
    page,
    perPage,
    changePage,
    changePerPage,
    setTotalItems,
    totalPages,
    nextDisabled,
    nextPage,
    prevDisabled,
    prevPage,
  } = usePagination();

  const { data, isLoading, isFetching } = useBillingGroups({ page, perPage });

  const redirectToCreateBillingGroupForm = () => {
    navigate({ to: '/billing-groups/create' });
  };

  const [selectedBillingGroupId, setSelectedBillingGroupId] = useState<number | null>(
    billingGroupId || null,
  );

  const handleSelectBillingGroup = (id: number) => {
    if (id === selectedBillingGroupId) {
      setSelectedBillingGroupId(null);
    } else setSelectedBillingGroupId(id);
  };

  const { mutateAsync: removeHostFromBillingGroup, isPending: isRemoving } =
    useRemoveHostFromBillingGroup();

  const { mutateAsync: attachHostToBillingGroup, isPending: isAttaching } =
    useAttachHostToBillingGroup();

  useEffect(() => {
    if (data) {
      setTotalItems(data?.total);
    }
  }, [data]);

  useEffect(() => {
    if (open) {
      setSelectedBillingGroupId(billingGroupId || null);
    }
  }, [open, billingGroupId]);

  const onConfirm = async () => {
    try {
      if (billingGroupId) {
        await removeHostFromBillingGroup({ billingGroupId, hostId });
      }

      if (selectedBillingGroupId !== null) {
        await attachHostToBillingGroup({ billingGroupId: selectedBillingGroupId, hostId });
      }

      onCancel?.();
    } catch {
      // Mutation hooks display errors; keep the dialog open.
    }
  };

  return (
    <DialogCore
      onClose={onCancel}
      open={open}
      PaperProps={{
        sx: {
          width: '100%',
        },
      }}>
      <DialogTitle
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}>
        <InfoIcon color="info" fontSize="large" />
        <Typography color="inherit" sx={{ ml: 2 }} variant="inherit">
          {t('hosts:modal.title2')}
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ my: 1 }}>
          {data?.resources?.map(group => {
            const { name, id } = group;

            return (
              <Card
                onClick={() => handleSelectBillingGroup(id)}
                key={id}
                sx={{
                  boxShadow: theme =>
                    selectedBillingGroupId === id
                      ? `0px 0px 0px 2px ${theme.palette.primary.main}`
                      : `0px 0px 0px 1px ${theme.palette.divider}`,
                  cursor: 'pointer',
                  mb: 1,
                }}>
                <CardContent
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                  }}>
                  <Box>
                    <Typography color="textPrimary" variant="h6">
                      {name}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
        <CompactPagination
          changePage={changePage}
          page={page}
          handlePerPageChange={changePerPage}
          perPage={perPage}
          isFetching={isFetching}
          totalPages={totalPages}
          disabled={isLoading}
          nextDisabled={nextDisabled}
          prevDisabled={prevDisabled}
          nextPage={nextPage}
          prevPage={prevPage}
        />
      </DialogContent>
      <Divider />
      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}>
        <Box>
          <Button color="primary" onClick={redirectToCreateBillingGroupForm} variant="contained">
            {t('billingGroups:create.title')}
          </Button>
        </Box>
        <Box>
          <Button color="primary" onClick={onCancel} variant="text">
            {t('common:button.back')}
          </Button>
          <LoadingButton
            loading={isRemoving || isAttaching}
            disabled={!hostId || selectedBillingGroupId === (billingGroupId || null)}
            color="primary"
            onClick={onConfirm}
            variant="contained">
            {t('common:button.confirm')}
          </LoadingButton>
        </Box>
      </DialogActions>
    </DialogCore>
  );
};
